import React, {useMemo} from "react";
import {Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography,} from "@mui/material";
import {Neighbourhood, UNASSIGNED_NEIGHBOURHOOD_ID} from "../../../api/Model";
import {mapArraySetState, mapSetState, SetState, toggleButtonEvent} from "../../../util/FormHandlers";
import DeskChip from "../../../components/desk-chip/DeskChip";
import EmployeeAvatar from "../../../components/employee-avatar/EmployeeAvatar";
import EmployeeLink from "../../../components/employee-link/EmployeeLink";
import NeighbourhoodColorIcon from "../../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import type {DeskNeighbourhoodMismatchProblem} from "./DeskNeighbourhoodMismatchWidget";
import {Opt} from "../../../util/Optional";
import {filterNotNull} from "../../../util/ArrayUtil";

export interface DeskNeighbourhoodMismatchFix {
    problem: DeskNeighbourhoodMismatchProblem;
    solution: {
        type: 'move-employee',
        fromNeighbourhoods: Neighbourhood[],
        toNeighbourhoods: Neighbourhood[],
    } | {
        type: 'move-desk',
        fromNeighbourhood: Opt<Neighbourhood>,
        toNeighbourhood: Opt<Neighbourhood>,
    } | {
        type: 'ignore',
    };
}

function isAssigned(neighbourhood: Opt<Neighbourhood>): neighbourhood is Neighbourhood {
    return neighbourhood != null && neighbourhood.id !== UNASSIGNED_NEIGHBOURHOOD_ID;
}

function NeighbourhoodLabel({neighbourhood, state}: {
    neighbourhood: Opt<Neighbourhood>,
    state: 'default' | 'selected' | 'removed',
}) {
    return (
        <Stack direction="row" alignItems="center" gap={0.5}>
            {neighbourhood && (
                <NeighbourhoodColorIcon
                    neighbourhood={neighbourhood}
                    size={16}
                    sx={{opacity: state === 'removed' ? 0.5 : 1}}
                />
            )}
            <Typography
                variant={state === 'selected' ? 'body1' : 'body2'}
                color={neighbourhood ? undefined : 'text.secondary'}
                sx={{
                    textDecoration: state === 'removed' ? 'line-through' : undefined,
                    textDecorationColor: state === 'removed' ? 'text.secondary' : undefined,
                }}
            >
                {neighbourhood?.name ?? 'No neighbourhood'}
            </Typography>
        </Stack>
    );
}

function NeighbourhoodLabels({neighbourhoods, state}: {
    neighbourhoods: Neighbourhood[],
    state: 'default' | 'selected' | 'removed',
}) {
    const assigned = neighbourhoods.filter(isAssigned);
    return assigned.length > 0 ? (
        <>
            {assigned.map((hood, hoodIndex) => (
                <NeighbourhoodLabel
                    key={hood.id}
                    neighbourhood={hood}
                    state={state !== 'selected' || hoodIndex === 0 ? state : 'default'}
                />
            ))}
        </>
    ) : (
        <NeighbourhoodLabel
            neighbourhood={null}
            state={state}
        />
    );
}

function MoveEmployeeTooltip({problem}: {
    problem: DeskNeighbourhoodMismatchProblem
}) {
    const solution = useMemo(
        () => solutions.moveEmployee(problem),
        [problem],
    );

    return (
        <Typography variant="body1">
            Move the employee from {solution.fromNeighbourhoods.length > 0 ? (
            <>
                {solution.fromNeighbourhoods.map((hood, index) => (
                    <span key={hood.id}>
                        {index > 0 ? ', ' : ''}<b>{hood.name}</b>
                    </span>
                ))}
            </>
        ) : (
            <b>No neighbourhood</b>
        )} to {solution.toNeighbourhoods.length > 0 ? (
            <>
                {solution.toNeighbourhoods.map((hood, index) => (
                    <span key={hood.id}>
                        {index > 0 ? ', ' : ''}<b>{hood.name}</b>
                    </span>
                ))}
            </>
        ) : (
            <b>No neighbourhood</b>
        )}.
        </Typography>
    )
}

function MoveDeskTooltip({problem}: {
    problem: DeskNeighbourhoodMismatchProblem,
}) {
    const solution = useMemo(
        () => solutions.moveDesk(problem),
        [problem],
    );

    return (
        <Typography variant="body1">
            Move the desk from <b>{solution.fromNeighbourhood?.name ?? 'No neighbourhood'}</b> to <b>{solution.toNeighbourhood?.name ?? 'No neighbourhood'}</b>.
        </Typography>
    );
}

const solutions = {
    moveEmployee(problem: DeskNeighbourhoodMismatchProblem): DeskNeighbourhoodMismatchFix['solution'] & {
        type: 'move-employee'
    } {
        return {
            type: 'move-employee',
            fromNeighbourhoods: problem.employeeHoods.filter(isAssigned),
            toNeighbourhoods: filterNotNull([problem.deskHood]),
        };
    },

    moveDesk(problem: DeskNeighbourhoodMismatchProblem): DeskNeighbourhoodMismatchFix['solution'] & {
        type: 'move-desk'
    } {
        return {
            type: 'move-desk',
            fromNeighbourhood: problem.deskHood,
            toNeighbourhood: problem.employeeHoods.filter(isAssigned)[0],
        };
    },

    ignore: {
        type: 'ignore' as const,
    },
};


export function createDeskNeighbourhoodMismatchFixForm(
    problems: DeskNeighbourhoodMismatchProblem[],
): DeskNeighbourhoodMismatchFix[] {
    return problems.map(problem => ({
        problem,
        solution: solutions.moveEmployee(problem),
    }));
}

export function FixPanel({form, setForm, index}: {
    form: DeskNeighbourhoodMismatchFix[],
    setForm: SetState<DeskNeighbourhoodMismatchFix[]>,
    index: number,
}) {
    const {problem, solution} = form[index];
    const handleSolutionTypeChange = useMemo(() => {
        const setFix = mapArraySetState(setForm, index);
        const setSolution = mapSetState(setFix, 'solution');
        return toggleButtonEvent((solutionType: DeskNeighbourhoodMismatchFix['solution']['type']) => {
            switch (solutionType) {
                case 'move-employee':
                    setSolution(solutions.moveEmployee(problem));
                    break;
                case 'move-desk':
                    setSolution(solutions.moveDesk(problem));
                    break;
                case 'ignore':
                    setSolution(solutions.ignore);
                    break;
            }
        });
    }, [index, setForm]);

    const employee = problem.desk.assignedTo;
    const deskHood = isAssigned(problem.deskHood) ? problem.deskHood : null;

    return (
        <Paper key={problem.desk.id} variant="outlined" sx={{p: 2}}>
            <Stack gap={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <Stack gap={0.5} minWidth={0}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <EmployeeAvatar employee={employee} sx={{width: 24, height: 24}}/>
                            <EmployeeLink employee={employee} variant="body2"/>
                        </Stack>
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                            <NeighbourhoodLabels
                                neighbourhoods={problem.employeeHoods}
                                state={
                                    solution.type === 'move-employee' ? 'removed'
                                        : solution.type === 'move-desk' ? 'selected'
                                            : 'default'
                                }
                            />
                        </Stack>
                    </Stack>
                    <Stack gap={0.5} alignItems="flex-end" minWidth={0}>
                        <DeskChip desk={problem.desk} deskAssignee={employee} hideAssignee size="small"/>
                        <NeighbourhoodLabel
                            neighbourhood={deskHood}
                            state={
                                solution.type === 'move-employee' ? 'selected'
                                    : solution.type === 'move-desk' ? 'removed'
                                        : 'default'
                            }
                        />
                    </Stack>
                </Stack>
                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={solution.type}
                    onChange={handleSolutionTypeChange}
                >
                    <Tooltip describeChild title={<MoveEmployeeTooltip problem={problem}/>}>
                        <ToggleButton value="move-employee">Move Employee</ToggleButton>
                    </Tooltip>
                    <Tooltip describeChild title={<MoveDeskTooltip problem={problem}/>}>
                        <ToggleButton value="move-desk">Move Desk</ToggleButton>
                    </Tooltip>
                    <Tooltip
                        describeChild
                        title={<Typography variant="body1">Leave this mismatch unresolved.</Typography>}
                    >
                        <ToggleButton value="ignore">Ignore</ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>
            </Stack>
        </Paper>
    );

}

export function DeskNeighbourhoodMismatchFixForm({form, setForm}: {
    form: DeskNeighbourhoodMismatchFix[],
    setForm: SetState<DeskNeighbourhoodMismatchFix[]>,
}) {
    return (
        <Stack gap={2}>
            {form.map((fix, index) => (
                <FixPanel key={fix.problem.desk.id} form={form} setForm={setForm} index={index}/>
            ))}
        </Stack>
    );
}
